import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient, Tenant, User } from '../../services/api';
import { Building2, Home, Users, Plus, Edit2, Trash2, Search, ArrowLeft, LogOut } from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ConfirmDialog, { useConfirmDialog } from '../../components/ui/ConfirmDialog';

type TabType = 'users' | 'settings';

export default function TenantEnvironment() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken, user, logout } = useAuth();

  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [tenantUsers, setTenantUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user' as 'user' | 'admin',
  });
  const [editUser, setEditUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user' as 'user' | 'admin',
    active: true,
  });
  const [feedback, setFeedback] = useState<{type: 'success' | 'error', message: string} | null>(null);
  const { confirm: showConfirm, dialogProps } = useConfirmDialog();

  // Verificar se o usuário é super admin
  if (user?.role !== 'super_admin') {
    navigate('/waba');
    return null;
  }

  // Buscar tenant atual
  useEffect(() => {
    const fetchTenant = async () => {
      if (!accessToken || !id) return;

      try {
        setIsLoading(true);
        const response = await apiClient.getTenant(id, accessToken);

        if (response.success) {
          setCurrentTenant(response.data);
          await loadTenantUsers();
        } else {
          setError('Empresa não encontrada');
          setTimeout(() => navigate('/admin/tenants'), 2000);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar empresa');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTenant();
  }, [accessToken, id, navigate]);

  const loadTenantUsers = async () => {
    if (!accessToken || !id) return;

    try {
      const response = await apiClient.getUsers(
        { tenantId: id, page: 1, limit: 100 },
        accessToken
      );

      if (response.success) {
        // Check if data is directly an array or nested in data.data
        const users = Array.isArray(response.data) ? response.data : response.data.data;
        setTenantUsers(users || []);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const handleCreateUser = async () => {
    if (!accessToken || !id) return;

    // Validação
    if (!newUser.name || !newUser.email || !newUser.password) {
      setFeedback({ type: 'error', message: 'Por favor, preencha todos os campos obrigatórios' });
      return;
    }

    if (newUser.password.length < 8) {
      setFeedback({ type: 'error', message: 'A senha deve ter no mínimo 8 caracteres' });
      return;
    }

    try {
      const response = await apiClient.createUser(
        {
          name: newUser.name,
          email: newUser.email,
          password: newUser.password,
          role: newUser.role,
          tenantId: id,
        },
        accessToken
      );

      if (response.success) {
        setFeedback({ type: 'success', message: 'Usuário criado com sucesso!' });
        setShowCreateModal(false);
        setNewUser({ name: '', email: '', password: '', role: 'user' });
        loadTenantUsers();
      } else {
        setFeedback({ type: 'error', message: `Erro ao criar usuário: ${(response as any).error?.message || 'Erro desconhecido'}` });
      }
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      setFeedback({ type: 'error', message: 'Erro ao criar usuário. Tente novamente.' });
    }
  };

  const handleOpenEditModal = (user: User) => {
    setSelectedUser(user);
    setEditUser({
      name: user.name,
      email: user.email,
      password: '', // Deixar vazio, só preencher se quiser alterar
      role: user.role as 'user' | 'admin',
      active: user.active,
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async () => {
    if (!accessToken || !selectedUser) return;

    // Validação
    if (!editUser.name || !editUser.email) {
      setFeedback({ type: 'error', message: 'Nome e email são obrigatórios' });
      return;
    }

    // Se a senha foi preenchida, validar
    if (editUser.password && editUser.password.length < 8) {
      setFeedback({ type: 'error', message: 'A senha deve ter no mínimo 8 caracteres' });
      return;
    }

    try {
      // Construir o payload apenas com campos que foram alterados
      const updateData: any = {
        name: editUser.name,
        email: editUser.email,
        role: editUser.role,
        active: editUser.active,
      };

      // Só incluir senha se foi preenchida
      if (editUser.password) {
        updateData.password = editUser.password;
      }

      const response = await apiClient.updateUser(
        selectedUser.id,
        updateData,
        accessToken,
        id // tenantId
      );

      if (response.success) {
        setFeedback({ type: 'success', message: 'Usuário atualizado com sucesso!' });
        setShowEditModal(false);
        setSelectedUser(null);
        setEditUser({ name: '', email: '', password: '', role: 'user', active: true });
        loadTenantUsers();
      } else {
        setFeedback({ type: 'error', message: `Erro ao atualizar usuário: ${(response as any).error?.message || 'Erro desconhecido'}` });
      }
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      setFeedback({ type: 'error', message: 'Erro ao atualizar usuário. Tente novamente.' });
    }
  };

  const handleDeleteUser = (userId: string) => {
    if (!accessToken || !id) return;

    showConfirm({
      title: 'Deletar usuário',
      message: 'Tem certeza que deseja deletar este usuário?',
      variant: 'danger',
      confirmText: 'Deletar',
      onConfirm: async () => {
        try {
          await apiClient.deleteUser(userId, accessToken, id);
          loadTenantUsers();
        } catch (error) {
          console.error('Erro ao deletar usuário:', error);
          setFeedback({ type: 'error', message: 'Erro ao deletar usuário. Tente novamente.' });
        }
      },
    });
  };

  const filteredUsers = (tenantUsers || []).filter(
    (u) =>
      u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadge = (role: string) => {
    const colors = {
      super_admin: 'bg-accent-2/10 text-accent-2',
      admin: 'bg-accent-1/10 text-accent-1',
      user: 'bg-success/10 text-success',
      viewer: 'bg-bg-3 text-text-1',
    };

    const labels = {
      super_admin: 'Super Admin',
      admin: 'Admin',
      user: 'Usuário',
      viewer: 'Visualizador',
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[role as keyof typeof colors] || colors.user}`}>
        {labels[role as keyof typeof labels] || role}
      </span>
    );
  };

  if (isLoading) {
    return <LoadingSpinner fullPage text="Carregando..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-bg-1 flex items-center justify-center">
        <div className="text-center">
          <p className="text-danger mb-4">{error}</p>
          <button
            onClick={() => navigate('/admin/tenants')}
            className="text-accent-1 hover:underline"
          >
            Voltar para empresas
          </button>
        </div>
      </div>
    );
  }

  if (!currentTenant) {
    return null;
  }

  const tabs = [
    { id: 'users', label: 'Usuários', icon: Users },
    { id: 'settings', label: 'Configurações', icon: Building2 },
  ] as const;

  return (
    <div className="min-h-screen bg-bg-1">
      {/* Header */}
      <header className="bg-bg-2 border-b border-border-1 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            {/* Voltar + Info do Tenant */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/tenants')}
                className="flex items-center gap-2 px-3 py-2 hover:bg-bg-3/50 rounded-md transition-colors text-text-2 hover:text-text-1"
              >
                <ArrowLeft size={18} />
                <span className="text-sm font-medium">Tenants</span>
              </button>

              {/* Info do Tenant */}
              <div className="flex items-center gap-3 px-4 py-2 bg-bg-3 rounded-md">
                <Building2 size={18} className="text-accent-1" />
                <div className="text-left min-w-0 flex-1">
                  <p className="text-xs text-text-3">Empresa Atual</p>
                  <p className="text-sm font-medium text-text-1">
                    {currentTenant.name}
                  </p>
                  <p className="text-xs text-text-2">
                    {currentTenant.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2 px-3 py-2 hover:bg-bg-3/50 rounded-md transition-colors text-text-2 hover:text-text-1"
              >
                <Home size={18} />
                <span className="text-sm font-medium">Dashboard</span>
              </button>
              <button
                onClick={() => {
                  logout();
                  navigate('/login');
                }}
                className="flex items-center gap-2 px-4 py-2 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors"
              >
                <LogOut size={16} />
                <span className="text-sm font-medium">Sair</span>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-4 flex gap-1 border-b border-border-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors relative ${
                    activeTab === tab.id
                      ? 'text-accent-1'
                      : 'text-text-2 hover:text-text-1'
                  }`}
                >
                  <Icon size={16} />
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent-1" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Feedback Banner */}
        {feedback && (
          <div className={`mb-4 p-3 rounded-lg flex items-center justify-between ${
            feedback.type === 'error'
              ? 'bg-danger/10 border border-danger/30 text-danger'
              : 'bg-success/10 border border-success/30 text-success'
          }`}>
            <span className="text-sm">{feedback.message}</span>
            <button onClick={() => setFeedback(null)} className="ml-2 hover:opacity-70">
              &times;
            </button>
          </div>
        )}

        {activeTab === 'users' && (
          <div>
            {/* Users Header */}
            <div className="mb-6 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-text-1">
                  Usuários da Empresa
                </h2>
                <p className="text-sm text-text-2 mt-1">
                  Total: {filteredUsers.length} usuário(s)
                </p>
              </div>

              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-accent-1 text-white rounded-lg hover:bg-accent-1/90"
              >
                <Plus size={16} />
                Novo Usuário
              </button>
            </div>

            {/* Search */}
            <div className="mb-6 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-3 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
              />
            </div>

            {/* Users Table */}
            <div className="bg-bg-2 rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-border-1">
                <thead className="bg-bg-3">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                      Usuário
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-3 uppercase tracking-wider">
                      Criado em
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-text-3 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-bg-2 divide-y divide-border-1">
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-text-3">
                        Nenhum usuário encontrado
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((u) => (
                      <tr key={u.id} className="hover:bg-bg-3/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-text-1">
                              {u.name}
                            </div>
                            <div className="text-sm text-text-3">{u.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getRoleBadge(u.role)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              u.active
                                ? 'bg-success/10 text-success'
                                : 'bg-danger/10 text-danger'
                            }`}
                          >
                            {u.active ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-text-3">
                            {new Date(u.createdAt).toLocaleDateString('pt-BR')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleOpenEditModal(u)}
                            className="text-accent-1 hover:text-accent-1/80 mr-4"
                            title="Editar usuário"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(u.id)}
                            className="text-danger hover:text-danger/80"
                            title="Deletar usuário"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="bg-bg-2 rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-text-1 mb-4">
              Configurações da Empresa
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-2 mb-1">
                  Nome
                </label>
                <input
                  type="text"
                  value={currentTenant.name}
                  disabled
                  className="w-full px-3 py-2 border border-border-1 rounded-lg bg-bg-3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-2 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={currentTenant.email}
                  disabled
                  className="w-full px-3 py-2 border border-border-1 rounded-lg bg-bg-3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-2 mb-1">
                  Status
                </label>
                <input
                  type="text"
                  value={currentTenant.status}
                  disabled
                  className="w-full px-3 py-2 border border-border-1 rounded-lg bg-bg-3 capitalize"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-2 mb-1">
                  Plano
                </label>
                <input
                  type="text"
                  value={currentTenant.tier}
                  disabled
                  className="w-full px-3 py-2 border border-border-1 rounded-lg bg-bg-3 capitalize"
                />
              </div>
              <p className="text-sm text-text-3 mt-4">
                Para editar estas configurações, volte para a lista de empresas e use a opção "Editar".
              </p>
            </div>
          </div>
        )}
      </main>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bg-1 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-text-1 mb-4">
              Criar Novo Usuário
            </h3>

            <div className="space-y-4">
              {/* Name Field */}
              <div>
                <label className="block text-sm font-medium text-text-2 mb-1">
                  Nome <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="Digite o nome completo"
                  className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
                />
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-text-2 mb-1">
                  Email <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="Digite o email"
                  className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
                />
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-text-2 mb-1">
                  Senha <span className="text-danger">*</span>
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Mínimo 8 caracteres"
                  className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
                />
                <p className="text-xs text-text-3 mt-1">
                  A senha deve ter no mínimo 8 caracteres
                </p>
              </div>

              {/* Role Field */}
              <div>
                <label className="block text-sm font-medium text-text-2 mb-1">
                  Função
                </label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value as 'user' | 'admin' })}
                  className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
                >
                  <option value="user">Usuário</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewUser({ name: '', email: '', password: '', role: 'user' });
                }}
                className="flex-1 px-4 py-2 border border-border-1 text-text-2 rounded-lg hover:bg-bg-3/50"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateUser}
                className="flex-1 px-4 py-2 bg-accent-1 text-white rounded-lg hover:bg-accent-1/90"
              >
                Criar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bg-1 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-text-1 mb-4">
              Editar Usuário
            </h3>

            <div className="space-y-4">
              {/* Name Field */}
              <div>
                <label className="block text-sm font-medium text-text-2 mb-1">
                  Nome <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={editUser.name}
                  onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                  placeholder="Digite o nome completo"
                  className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
                />
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-text-2 mb-1">
                  Email <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  value={editUser.email}
                  onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                  placeholder="Digite o email"
                  className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
                />
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-text-2 mb-1">
                  Nova Senha <span className="text-text-3">(opcional)</span>
                </label>
                <input
                  type="password"
                  value={editUser.password}
                  onChange={(e) => setEditUser({ ...editUser, password: e.target.value })}
                  placeholder="Deixe em branco para manter a senha atual"
                  className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
                />
                <p className="text-xs text-text-3 mt-1">
                  Preencha apenas se desejar alterar a senha (mínimo 8 caracteres)
                </p>
              </div>

              {/* Role Field */}
              <div>
                <label className="block text-sm font-medium text-text-2 mb-1">
                  Função
                </label>
                <select
                  value={editUser.role}
                  onChange={(e) => setEditUser({ ...editUser, role: e.target.value as 'user' | 'admin' })}
                  className="w-full px-3 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
                >
                  <option value="user">Usuário</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {/* Active Status */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editUser.active}
                    onChange={(e) => setEditUser({ ...editUser, active: e.target.checked })}
                    className="w-4 h-4 text-accent-1 border-border-1 rounded focus:ring-accent-1"
                  />
                  <span className="text-sm font-medium text-text-2">
                    Usuário ativo
                  </span>
                </label>
                <p className="text-xs text-text-3 mt-1">
                  Usuários inativos não podem fazer login no sistema
                </p>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedUser(null);
                  setEditUser({ name: '', email: '', password: '', role: 'user', active: true });
                }}
                className="flex-1 px-4 py-2 border border-border-1 text-text-2 rounded-lg hover:bg-bg-3/50"
              >
                Cancelar
              </button>
              <button
                onClick={handleUpdateUser}
                className="flex-1 px-4 py-2 bg-accent-1 text-white rounded-lg hover:bg-accent-1/90"
              >
                Salvar Alterações
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog {...dialogProps} />
    </div>
  );
}
